import type { User } from "firebase/auth";
import { mountCharacterIdlePreview } from "@/character/character-preview";
import { equippedPet, loadPetState } from "@/pet/firestore-storage";
import { buildPetSkillRuntimePayload } from "@/pet/skills/runtime";
import { equippedShadow, loadShadowState } from "@/shadow/firestore-storage";
import { loadGameArtForUser } from "@/shadow/merge-game-art";
import { shadowEquippedStatBonus } from "@/shadow/shadow-stat-bonus";
import { equipmentStatBonus, loadEquipmentState } from "@/character/equipment/storage";
import { loadCharacterProfile } from "@/character/storage";
import {
  loadCharacterProgression,
  progressionXpDisplay,
  type CharacterProgression,
} from "@/game/model/character-progression";
import { mergeStatBonuses, playerCombatMaxHp, playerRatings } from "@/game/model/character-stats";
import {
  combatManaRegenPerSec,
  playerCombatMaxMana,
} from "@/game/model/combat-mana";
import { applyHpRegenTick } from "@/game/model/player-hp-regen";
import { grantExpForUser } from "@/game/model/grant-exp";
import {
  KillStreakTracker,
  type KillStreakState,
} from "@/game/model/kill-streak";
import { startTowerBattle } from "@/game/main";
import type Phaser from "phaser";
import { HomeEvents } from "@/home/home-events";
import {
  buyReviveCardMock,
  createDefaultHomeEconomy,
  REVIVE_CARD_CASH_COST,
  setCombatHpCurrent,
  setCombatManaCurrent,
  setItemHotbarSlot,
  setItemHotbarSlotAuto,
  shouldPersistCombatHp,
  shouldPersistCombatMana,
  useHealthPotion,
  useManaPotion,
  useReviveCard,
  type HomeEconomy,
} from "@/home/economy";
import { tierFromStashItemId } from "@/home/health-potions";
import { tierFromManaStashItemId } from "@/home/mana-potions";
import {
  bindItemHotbar,
  hotbarUseBlockedReason,
  syncItemHotbar,
  syncItemHotbarCooldownState,
} from "@/home/home-item-hotbar";
import {
  itemHotbarUseReady,
  itemHotbarAutoUseReady,
  markItemHotbarAutoUsed,
  markItemHotbarUsed,
  pickAutoHotbarManaPotionSlot,
  pickAutoHotbarPotionSlot,
  tryAutoHotbarPotionOnce,
} from "@/home/item-hotbar-use";
import type { StashItemId } from "@/home/home-stash-modal";
import { hydrateGmShopCatalog } from "@/gm/gm-shop-catalog";
import { HOME_ASSETS } from "@/home/home-assets";
import { loadHomeEconomyFromIdb, saveHomeEconomyToIdb } from "@/home/home-idb";
import { hydrateGmMonsterCatalog } from "@/gm/gm-monster-catalog";
import {
  buildTowerFloorBattlePlan,
  towerFloorLobbyView,
} from "@/tower/tower-floor-runtime";
import {
  hydrateTowerFloorConfigs,
  towerFloorPlayBlockReason,
} from "@/tower/tower-floor-config";
import { TOWER_MAX_FLOOR, towerXpForClear } from "@/tower/tower-floors";
import {
  TowerEvents,
  type TowerBattleEndedPayload,
  type TowerPlayerHpChangedPayload,
  type TowerPlayerManaChangedPayload,
} from "@/tower/tower-events";
import {
  loadTowerProgress,
  maxSelectableFloor,
  saveTowerProgress,
  type TowerProgress,
} from "@/tower/tower-storage";
import { towerCombatBarMarkup } from "@/tower/tower-combat-overlay";
import { mountTowerSkillBar, type TowerSkillBarHandle } from "@/tower/tower-skill-bar";
import { syncPlayerPublicStats } from "@/cloud/sync-player-stats";
import { loadMyRankCosmetics } from "@/rank/load-rank-state";

const TOWER_LOBBY_BG = "/assets/images/tower/bg_tower.png";
const TOWER_LOBBY_CHAR_PX = 168;

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export type MountTowerPageOptions = {
  asModal?: boolean;
  onRequestClose?: () => void;
  onBattleActiveChange?: (active: boolean) => void;
};

export async function mountTowerPage(
  root: HTMLElement,
  user: User,
  options?: MountTowerPageOptions,
): Promise<() => void> {
  const profile = loadCharacterProfile(user.uid);
  if (!profile) {
    window.location.replace("/character/create");
    return () => {};
  }

  await hydrateGmMonsterCatalog();
  await hydrateGmShopCatalog();
  await hydrateTowerFloorConfigs();

  if (options?.asModal) {
    root.classList.add("page-tower");
  } else {
    root.className = "game-app page-tower";
  }

  let progression: CharacterProgression = loadCharacterProgression(user.uid);
  let towerProgress: TowerProgress = loadTowerProgress(user.uid);
  let selectedFloor = Math.min(maxSelectableFloor(towerProgress), TOWER_MAX_FLOOR);
  let game: Phaser.Game | null = null;
  let battleRunning = false;

  const tryRequestClose = () => {
    if (battleRunning) return;
    options?.onRequestClose?.();
  };
  let skillBarHud: TowerSkillBarHandle | null = null;
  let autoChaseOn = false;
  let killStreakTracker: KillStreakTracker | null = null;
  let stopLobbyCharPreview: (() => void) | null = null;
  let playerCombatHp = { current: 0, max: 1 };
  let playerCombatMana = { current: 0, max: 1 };
  let playerDowned = false;
  let lobbyManaRegenCarry = 0;

  let economy: HomeEconomy =
    (await loadHomeEconomyFromIdb(user.uid)) ?? createDefaultHomeEconomy();

  const persistEconomy = () => {
    void saveHomeEconomyToIdb(user.uid, economy);
    window.dispatchEvent(
      new CustomEvent(HomeEvents.EconomyChanged, { detail: { economy } }),
    );
  };

  let rankCosmetics = await loadMyRankCosmetics();
  let shadowState = await loadShadowState(user.uid);
  let petState = await loadPetState(user.uid);
  const equipmentBonus = () =>
    mergeStatBonuses(
      equipmentStatBonus(loadEquipmentState(user.uid)),
      shadowEquippedStatBonus(equippedShadow(shadowState)),
      rankCosmetics.statBonus,
    );

  const commitCombatHp = (current: number) => {
    const max = playerCombatMaxHp(playerRatings(progression, equipmentBonus()));
    const clamped = Math.max(0, Math.min(max, Math.floor(current)));
    if (!shouldPersistCombatHp(economy, clamped, max)) return;
    const next = setCombatHpCurrent(economy, clamped);
    if (next === economy) return;
    economy = next;
    persistEconomy();
  };

  const commitCombatMana = (current: number) => {
    const max = playerCombatMaxMana(playerRatings(progression, equipmentBonus()));
    const clamped = Math.max(0, Math.min(max, Math.floor(current)));
    if (!shouldPersistCombatMana(economy, clamped, max)) return;
    const next = setCombatManaCurrent(economy, clamped);
    if (next === economy) return;
    economy = next;
    persistEconomy();
  };

  const syncPlayerCombatManaFromEconomy = () => {
    const max = playerCombatMaxMana(playerRatings(progression, equipmentBonus()));
    const raw = economy.combatManaCurrent !== null ? economy.combatManaCurrent : max;
    playerCombatMana = {
      current: Math.max(0, Math.min(max, raw)),
      max,
    };
  };

  const tickLobbyManaRegen = () => {
    if (battleRunning) return;
    const max = playerCombatMaxMana(playerRatings(progression, equipmentBonus()));
    const raw = economy.combatManaCurrent !== null ? economy.combatManaCurrent : max;
    const current = Math.max(0, Math.min(max, raw));
    if (current >= max) return;
    const result = applyHpRegenTick(current, max, combatManaRegenPerSec(), lobbyManaRegenCarry);
    lobbyManaRegenCarry = result.carry;
    if (result.healed <= 0) return;
    commitCombatMana(result.current);
    syncPlayerCombatManaFromEconomy();
  };

  const syncBattleChrome = () => {
    root.classList.toggle("page-tower--battle", battleRunning);
    options?.onBattleActiveChange?.(battleRunning);
  };

  const mountLobbyCharacter = () => {
    stopLobbyCharPreview?.();
    stopLobbyCharPreview = null;
    const host = root.querySelector<HTMLElement>("#tower-lobby-char-preview");
    if (!host || battleRunning) return;
    stopLobbyCharPreview = mountCharacterIdlePreview(host, profile, {
      displaySizePx: TOWER_LOBBY_CHAR_PX,
    });
  };

  const renderLobby = () => {
    const lobbyEl = root.querySelector<HTMLElement>("#tower-lobby");
    if (!lobbyEl) return;
    const resultText =
      root.querySelector<HTMLElement>("#tower-result")?.textContent?.trim() ?? "";
    selectedFloor = maxSelectableFloor(towerProgress);
    const xp = progressionXpDisplay(progression);
    const view = towerFloorLobbyView(selectedFloor, towerProgress.highestCleared, TOWER_MAX_FLOOR);
    const ratings = playerRatings(progression, equipmentBonus());
    const bossClass = view.floor % 10 === 0 ? " tower-lobby--boss" : "";

    stopLobbyCharPreview?.();
    stopLobbyCharPreview = null;

    lobbyEl.className = `tower-lobby${bossClass}`;
    lobbyEl.innerHTML = `
      <div class="tower-lobby-bg" style="background-image: url('${TOWER_LOBBY_BG}')" aria-hidden="true"></div>
      <div class="tower-lobby-vignette" aria-hidden="true"></div>

      <div class="tower-lobby-scene">
        <header class="tower-lobby-topbar">
          ${
            options?.asModal
              ? `<button type="button" class="tower-lobby-home" id="tower-close-btn">× ปิด</button>`
              : `<a class="tower-lobby-home" href="/home">← บ้าน</a>`
          }
          <div class="tower-lobby-wallet" title="Cash">
            <img src="${HOME_ASSETS.coin}" width="20" height="20" alt="" />
            <span class="tower-lobby-wallet-label">ทอง</span>
            <span class="tower-lobby-wallet-value">${economy.wallet.cash.toLocaleString("en-US")}</span>
            <span class="tower-lobby-wallet-unit">ชิ้น</span>
          </div>
        </header>

        <div class="tower-lobby-hero" aria-live="polite">
          <aside class="tower-lobby-level-badge">
            <p class="tower-lobby-level-kicker">นักผจญภัย</p>
            <p class="tower-lobby-level-num">Lv.${progression.level}</p>
            <p class="tower-lobby-level-exp">EXP ${xp.totalExp.toLocaleString("en-US")}</p>
          </aside>

          <div class="tower-lobby-char-stage" id="tower-lobby-char-preview"></div>

          <aside class="tower-lobby-floor-badge">
            <div class="tower-lobby-floor-ring">
              <span class="tower-lobby-floor-unit">ชั้น</span>
              <span class="tower-lobby-floor-num" id="tower-floor-label">${view.floor}</span>
            </div>
            <p class="tower-lobby-floor-name">${escapeHtml(view.name)}</p>
          </aside>
        </div>

        <footer class="tower-lobby-footer">
          <div class="tower-lobby-stats">
            <article class="tower-stat-card tower-stat-card--attack">
              <span class="tower-stat-icon" aria-hidden="true">⚔</span>
              <p class="tower-stat-label">พลังโจมตี</p>
              <p class="tower-stat-value">${ratings.attack.toLocaleString("en-US")}</p>
            </article>
            <article class="tower-stat-card tower-stat-card--hp">
              <span class="tower-stat-icon" aria-hidden="true">♥</span>
              <p class="tower-stat-label">HP รวม</p>
              <p class="tower-stat-value">${view.totalHp.toLocaleString("en-US")}</p>
            </article>
            <article class="tower-stat-card tower-stat-card--gold">
              <span class="tower-stat-icon" aria-hidden="true">✦</span>
              <p class="tower-stat-label">รางวัล EXP</p>
              <p class="tower-stat-value">${view.xpFirst.toLocaleString("en-US")}</p>
            </article>
          </div>

          <p class="hint tower-result" id="tower-result" role="status">${escapeHtml(resultText)}</p>

          <button type="button" class="tower-lobby-cta" id="tower-start-btn" ${battleRunning ? "disabled" : ""}>
            <span class="tower-lobby-cta-glow" aria-hidden="true"></span>
            <span class="tower-lobby-cta-icon" aria-hidden="true">⚔</span>
            <span class="tower-lobby-cta-text">${battleRunning ? "กำลังต่อสู้…" : "เข้าสู่การต่อสู้"}</span>
          </button>
        </footer>
      </div>
    `;

    mountLobbyCharacter();
  };

  const renderTowerUi = () => {
    renderLobby();
    syncBattleChrome();
  };

  root.innerHTML = `
    <div class="tower-layout">
      <div class="tower-stage">
        <section id="tower-lobby" class="tower-lobby" aria-label="หน้าเตรียมเข้าต่อสู้"></section>
        <div id="tower-game-container" class="tower-game-container" aria-label="ฉากต่อสู้" hidden></div>
        ${towerCombatBarMarkup()}
      </div>
    </div>
    <div id="tower-revive-modal" class="tower-revive-modal" hidden aria-hidden="true">
      <button type="button" class="tower-revive-backdrop" id="tower-revive-backdrop" aria-label="ปิด"></button>
      <div class="tower-revive-dialog" role="dialog" aria-modal="true" aria-labelledby="tower-revive-title">
        <h2 id="tower-revive-title" class="tower-revive-title">คุณล้มในสนามรบ</h2>
        <p class="tower-revive-lede">ต้องการชุบชีวิตเพื่อสู้ต่อหรือยอมแพ้ชั้นนี้?</p>
        <p class="hint tower-revive-meta">บัตรชุบในคลัง: <strong id="tower-revive-card-count">0</strong> ใบ</p>
        <p class="hint tower-revive-status" id="tower-revive-status" role="status"></p>
        <div class="tower-revive-actions">
          <button type="button" class="btn btn-primary" id="tower-revive-use-card">ชุบชีวิต (ใช้บัตร 1 ใบ)</button>
          <button type="button" class="btn btn-ghost" id="tower-revive-give-up">ยอมแพ้</button>
          <div class="tower-revive-buy-row">
            <p class="hint">ซื้อบัตรชุบ: ${REVIVE_CARD_CASH_COST.toLocaleString("en-US")} cash · คุณมี <span id="tower-revive-cash">${economy.wallet.cash.toLocaleString("en-US")}</span> cash</p>
            <button type="button" class="btn btn-ghost btn-sm" id="tower-revive-buy-card">ซื้อบัตรชุบ</button>
            <a class="hint text-link" href="/home?shop=revive">หรือซื้อที่บ้าน · ร้าน</a>
          </div>
        </div>
      </div>
    </div>
  `;

  skillBarHud = await mountTowerSkillBar(root, user.uid, () => battleRunning);

  const syncItemHotbarHud = () => {
    syncItemHotbar(root, economy, "tower", {
      disabled: !battleRunning || playerDowned,
      itemUseCooldown: !itemHotbarUseReady(),
    });
    syncItemHotbarCooldownState(root, "tower");
  };

  const applyTowerHotbarPotion = (
    itemId: StashItemId,
    options?: { silent?: boolean; skipCooldown?: boolean },
  ): boolean => {
    if (!battleRunning || playerDowned) return false;
    if (!options?.skipCooldown && !itemHotbarUseReady()) {
      if (!options?.silent) syncItemHotbarHud();
      return false;
    }
    const blockReason = hotbarUseBlockedReason(itemId);
    if (blockReason) return false;
    const healthTier = tierFromStashItemId(itemId);
    if (healthTier) {
      const result = useHealthPotion(economy, healthTier, playerCombatHp);
      if (!result.ok) return false;
      economy = result.economy;
      if (!options?.skipCooldown) {
        markItemHotbarUsed();
      }
      playerCombatHp = {
        current: Math.min(playerCombatHp.max, playerCombatHp.current + result.healed),
        max: playerCombatHp.max,
      };
      commitCombatHp(playerCombatHp.current);
      persistEconomy();
      window.dispatchEvent(
        new CustomEvent(TowerEvents.HealthPotionHeal, {
          detail: { amount: result.healed },
        }),
      );
      syncItemHotbarHud();
      return result.healed > 0;
    }
    const manaTier = tierFromManaStashItemId(itemId);
    if (!manaTier) return false;
    const manaResult = useManaPotion(economy, manaTier, playerCombatMana);
    if (!manaResult.ok) return false;
    economy = manaResult.economy;
    if (!options?.skipCooldown) {
      markItemHotbarUsed();
    }
    playerCombatMana = {
      current: Math.min(playerCombatMana.max, playerCombatMana.current + manaResult.restored),
      max: playerCombatMana.max,
    };
    commitCombatMana(playerCombatMana.current);
    persistEconomy();
    syncItemHotbarHud();
    return manaResult.restored > 0;
  };

  const useTowerHotbarItem = (itemId: StashItemId, options?: { silent?: boolean }) => {
    applyTowerHotbarPotion(itemId, options);
  };

  const tryAutoTowerItemHotbar = () => {
    if (!battleRunning || playerDowned) return;
    if (!itemHotbarAutoUseReady()) return;
    const used = tryAutoHotbarPotionOnce(
      economy.itemHotbarAutoHpPercent,
      () => playerCombatHp,
      () => pickAutoHotbarPotionSlot(economy),
      economy.itemHotbarAutoManaPercent,
      () => playerCombatMana,
      () => pickAutoHotbarManaPotionSlot(economy),
      (itemId) => applyTowerHotbarPotion(itemId, { silent: true, skipCooldown: true }),
    );
    if (used) {
      markItemHotbarAutoUsed();
    }
  };

  const unbindItemHotbar = bindItemHotbar(root, {
    prefix: "tower",
    getEconomy: () => economy,
    onAssign: (index, itemId) => {
      economy = setItemHotbarSlot(economy, index, itemId);
      persistEconomy();
      syncItemHotbarHud();
    },
    onUseItem: useTowerHotbarItem,
    onSlotAutoChange: (index, enabled) => {
      economy = setItemHotbarSlotAuto(economy, index, enabled);
      persistEconomy();
      syncItemHotbarHud();
    },
    isUseBlocked: () => !battleRunning || playerDowned,
  });

  const itemHotbarAutoTimer = setInterval(() => {
    tickLobbyManaRegen();
    syncItemHotbarHud();
    tryAutoTowerItemHotbar();
  }, 250);

  const syncAutoButton = () => {
    const btn = root.querySelector<HTMLButtonElement>("#tower-auto-btn");
    if (!btn) return;
    btn.disabled = !battleRunning;
    btn.classList.toggle("tower-auto-btn--on", autoChaseOn && battleRunning);
    btn.setAttribute("aria-pressed", autoChaseOn && battleRunning ? "true" : "false");
  };

  const syncSurrenderButton = () => {
    const btn = root.querySelector<HTMLButtonElement>("#tower-surrender-btn");
    if (!btn) return;
    btn.disabled = !battleRunning;
  };

  const syncKillStreakHud = (state: KillStreakState) => {
    const currentEl = root.querySelector<HTMLElement>("#tower-kill-streak-current");
    const bestEl = root.querySelector<HTMLElement>("#tower-kill-streak-best");
    const wrap = root.querySelector<HTMLElement>("#tower-kill-streak");
    if (currentEl) currentEl.textContent = String(state.current);
    if (bestEl) bestEl.textContent = String(state.best);
    wrap?.classList.toggle("tower-kill-streak--active", state.current > 0);
  };

  const stopKillStreakTracker = () => {
    killStreakTracker?.destroy();
    killStreakTracker = null;
  };

  const startKillStreakTracker = () => {
    stopKillStreakTracker();
    killStreakTracker = new KillStreakTracker(user.uid, syncKillStreakHud);
  };

  const onTowerEnemyDefeated = () => {
    killStreakTracker?.recordKill();
  };

  const setAutoChase = (enabled: boolean) => {
    autoChaseOn = enabled;
    syncAutoButton();
    if (battleRunning) {
      window.dispatchEvent(
        new CustomEvent(TowerEvents.AutoChaseChanged, { detail: { enabled: autoChaseOn } }),
      );
    }
  };

  const destroyGame = () => {
    if (autoChaseOn) {
      autoChaseOn = false;
      window.dispatchEvent(
        new CustomEvent(TowerEvents.AutoChaseChanged, { detail: { enabled: false } }),
      );
    }
    game?.destroy(true);
    game = null;
    stopKillStreakTracker();
    battleRunning = false;
    const container = root.querySelector<HTMLElement>("#tower-game-container");
    const combatBar = root.querySelector<HTMLElement>("#tower-combat-bar");
    if (container) container.hidden = true;
    if (combatBar) combatBar.hidden = true;
    syncAutoButton();
    syncSurrenderButton();
    syncBattleChrome();
  };

  const startBattle = () => {
    void (async () => {
      const maxPick = maxSelectableFloor(towerProgress);
      selectedFloor = Math.min(maxPick, Math.max(1, selectedFloor));
      await hydrateTowerFloorConfigs();
      const playBlock = towerFloorPlayBlockReason(selectedFloor);
      const resultEl = root.querySelector<HTMLElement>("#tower-result");
      if (playBlock) {
        if (resultEl) resultEl.textContent = playBlock;
        return;
      }
      destroyGame();
      battleRunning = true;
      playerDowned = false;
      if (resultEl) resultEl.textContent = "";
      renderTowerUi();
      skillBarHud?.refresh();
      syncItemHotbarHud();
      syncAutoButton();
      syncSurrenderButton();

      const container = root.querySelector<HTMLElement>("#tower-game-container");
      const combatBar = root.querySelector<HTMLElement>("#tower-combat-bar");
      if (!container) return;
      container.hidden = false;
      if (combatBar) combatBar.hidden = false;
      container.innerHTML = "";

      const plan = await buildTowerFloorBattlePlan(selectedFloor);
      const art = await loadGameArtForUser(user.uid, profile);
      syncPlayerCombatManaFromEconomy();
      const activePet = equippedPet(petState);
      game = startTowerBattle(container, art, {
        towerFloor: selectedFloor,
        progression,
        equipmentStatBonus: equipmentBonus(),
        towerBattlePlan: plan,
        skillRuntime: skillBarHud?.getRuntime(),
        rankBadgeBoards: rankCosmetics.badgeBoards,
        rankBadgeConfigs: rankCosmetics.badgeConfigs,
        combatManaCurrent: economy.combatManaCurrent,
        equippedPet: activePet,
        petSkillRuntime: activePet
          ? buildPetSkillRuntimePayload(activePet.skillBar, economy)
          : undefined,
      });
      startKillStreakTracker();
    })();
  };

  const onFloorCleared = (event: Event) => {
    const detail = (event as CustomEvent<{ floor: number; baseXp: number }>).detail;
    const before = towerProgress.highestCleared;
    const { xp, firstClear } = towerXpForClear(detail.floor, before, detail.baseXp);
    progression = grantExpForUser(user.uid, xp);
    if (detail.floor > towerProgress.highestCleared) {
      towerProgress = {
        ...towerProgress,
        highestCleared: detail.floor,
      };
      saveTowerProgress(user.uid, towerProgress);
      void syncPlayerPublicStats(user);
      if (detail.floor < TOWER_MAX_FLOOR) {
        selectedFloor = detail.floor + 1;
      }
    }
    battleRunning = false;
    destroyGame();
    const resultEl = root.querySelector<HTMLElement>("#tower-result");
    if (resultEl) {
      resultEl.textContent = firstClear
        ? `ชนะชั้น ${detail.floor}! +${xp.toLocaleString("en-US")} EXP (ครั้งแรก)`
        : `ชนะชั้น ${detail.floor}! +${xp.toLocaleString("en-US")} EXP`;
    }
    renderTowerUi();
    skillBarHud?.refresh();
  };

  const reviveModal = () => root.querySelector<HTMLElement>("#tower-revive-modal");
  const setReviveStatus = (message: string, isError = false) => {
    const el = root.querySelector<HTMLElement>("#tower-revive-status");
    if (!el) return;
    el.textContent = message;
    el.classList.toggle("tower-revive-status--error", isError);
  };

  const syncReviveModalUi = () => {
    const countEl = root.querySelector<HTMLElement>("#tower-revive-card-count");
    const cashEl = root.querySelector<HTMLElement>("#tower-revive-cash");
    const useBtn = root.querySelector<HTMLButtonElement>("#tower-revive-use-card");
    const buyBtn = root.querySelector<HTMLButtonElement>("#tower-revive-buy-card");
    if (countEl) countEl.textContent = String(economy.wallet.reviveCards);
    if (cashEl) cashEl.textContent = economy.wallet.cash.toLocaleString("en-US");
    if (useBtn) useBtn.disabled = economy.wallet.reviveCards <= 0;
    if (buyBtn) buyBtn.disabled = economy.wallet.cash < REVIVE_CARD_CASH_COST;
  };

  const openReviveModal = () => {
    const modal = reviveModal();
    if (!modal) return;
    setReviveStatus("");
    syncReviveModalUi();
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
  };

  const closeReviveModal = () => {
    const modal = reviveModal();
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    setReviveStatus("");
  };

  const onPlayerDowned = () => {
    playerDowned = true;
    syncItemHotbarHud();
    openReviveModal();
  };

  const onPlayerHpChanged = (event: Event) => {
    const detail = (event as CustomEvent<TowerPlayerHpChangedPayload>).detail;
    if (!detail) return;
    playerCombatHp = { current: detail.current, max: detail.max };
    commitCombatHp(detail.current);
    syncItemHotbarHud();
    tryAutoTowerItemHotbar();
  };

  const onPlayerManaChanged = (event: Event) => {
    const detail = (event as CustomEvent<TowerPlayerManaChangedPayload>).detail;
    if (!detail) return;
    playerCombatMana = { current: detail.current, max: detail.max };
    commitCombatMana(detail.current);
    syncItemHotbarHud();
    tryAutoTowerItemHotbar();
  };

  const onReviveAccepted = () => {
    playerDowned = false;
    syncItemHotbarHud();
  };

  const onBattleEnded = (event: Event) => {
    const detail = (event as CustomEvent<TowerBattleEndedPayload>).detail;
    if (detail.victory) return;
    closeReviveModal();
    battleRunning = false;
    destroyGame();
    const resultEl = root.querySelector<HTMLElement>("#tower-result");
    if (resultEl) {
      resultEl.textContent = detail.surrendered
        ? `ยอมแพ้ชั้น ${detail.floor} — กลับหน้าเลือกชั้นแล้ว`
        : `แพ้ชั้น ${detail.floor} — ลองใหม่หรือเลเวลอัปก่อน`;
    }
    renderTowerUi();
    skillBarHud?.refresh();
  };

  const onProgressionChanged = (event: Event) => {
    progression = (event as CustomEvent<{ progression: CharacterProgression }>).detail.progression;
    renderTowerUi();
  };

  const onClick = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.id === "tower-start-btn" || target.closest("#tower-start-btn")) {
      if (!battleRunning) startBattle();
      return;
    }
    if (target.id === "tower-auto-btn" || target.closest("#tower-auto-btn")) {
      if (!battleRunning) return;
      setAutoChase(!autoChaseOn);
      return;
    }
    if (target.id === "tower-surrender-btn" || target.closest("#tower-surrender-btn")) {
      if (!battleRunning) return;
      if (!window.confirm("ยอมแพ้และกลับหน้าเลือกชั้น?")) return;
      window.dispatchEvent(new CustomEvent(TowerEvents.BattleGiveUp));
      return;
    }
    if (target.id === "tower-revive-use-card" || target.closest("#tower-revive-use-card")) {
      const used = useReviveCard(economy);
      if (!used.ok) {
        setReviveStatus(used.reason, true);
        syncReviveModalUi();
        return;
      }
      economy = used.economy;
      persistEconomy();
      closeReviveModal();
      window.dispatchEvent(new CustomEvent(TowerEvents.ReviveAccepted));
      setReviveStatus("");
      return;
    }
    if (target.id === "tower-revive-give-up" || target.closest("#tower-revive-give-up")) {
      closeReviveModal();
      window.dispatchEvent(new CustomEvent(TowerEvents.BattleGiveUp));
      return;
    }
    if (target.id === "tower-revive-buy-card" || target.closest("#tower-revive-buy-card")) {
      const bought = buyReviveCardMock(economy);
      if (!bought.ok) {
        setReviveStatus(bought.reason, true);
        syncReviveModalUi();
        return;
      }
      economy = bought.economy;
      persistEconomy();
      setReviveStatus(`ซื้อบัตรชุบแล้ว · ใช้ ${bought.cashSpent} cash`);
      syncReviveModalUi();
      return;
    }
    if (target.id === "tower-close-btn" || target.closest("#tower-close-btn")) {
      tryRequestClose();
    }
  };

  root.addEventListener("click", onClick);
  const onEconomyChanged = (event: Event) => {
    const detail = (event as CustomEvent<{ economy: HomeEconomy }>).detail;
    if (!detail?.economy) return;
    economy = detail.economy;
    syncReviveModalUi();
    syncItemHotbarHud();
    if (!battleRunning) renderTowerUi();
  };

  window.addEventListener(TowerEvents.FloorCleared, onFloorCleared);
  window.addEventListener(TowerEvents.BattleEnded, onBattleEnded);
  window.addEventListener(TowerEvents.PlayerDowned, onPlayerDowned);
  window.addEventListener(TowerEvents.PlayerHpChanged, onPlayerHpChanged);
  window.addEventListener(TowerEvents.PlayerManaChanged, onPlayerManaChanged);
  window.addEventListener(TowerEvents.ReviveAccepted, onReviveAccepted);
  window.addEventListener(TowerEvents.EnemyDefeated, onTowerEnemyDefeated);
  window.addEventListener(HomeEvents.ProgressionChanged, onProgressionChanged);
  window.addEventListener(HomeEvents.EconomyChanged, onEconomyChanged);

  syncPlayerCombatManaFromEconomy();
  renderTowerUi();
  syncAutoButton();
  syncSurrenderButton();
  syncItemHotbarHud();

  return () => {
    root.removeEventListener("click", onClick);
    window.removeEventListener(TowerEvents.FloorCleared, onFloorCleared);
    window.removeEventListener(TowerEvents.BattleEnded, onBattleEnded);
    window.removeEventListener(TowerEvents.PlayerDowned, onPlayerDowned);
    window.removeEventListener(TowerEvents.PlayerHpChanged, onPlayerHpChanged);
    window.removeEventListener(TowerEvents.PlayerManaChanged, onPlayerManaChanged);
    window.removeEventListener(TowerEvents.ReviveAccepted, onReviveAccepted);
    window.removeEventListener(TowerEvents.EnemyDefeated, onTowerEnemyDefeated);
    window.removeEventListener(HomeEvents.ProgressionChanged, onProgressionChanged);
    window.removeEventListener(HomeEvents.EconomyChanged, onEconomyChanged);
    stopLobbyCharPreview?.();
    stopLobbyCharPreview = null;
    persistEconomy();
    clearInterval(itemHotbarAutoTimer);
    unbindItemHotbar();
    destroyGame();
    skillBarHud?.destroy();
    root.innerHTML = "";
  };
}
